<div class="dh">
  <div class="dh-panel">
    <section class="dh-section" aria-labelledby="model-rs-heading">
      <div class="dh-section__intro">
        <h2 id="model-rs-heading" class="dh-section__title">Choose a resource server</h2>
        <p class="dh-section__lede">
          Pick where this model will live. You can add metadata and upload data in the next steps.
        </p>
      </div>

      <div class="dh-fieldset dh-fieldset--resource-servers" role="radiogroup" aria-labelledby="model-rs-heading">
        <div class="dh-servers">
          @for (s of servers; track s.id) {
            <label
              class="dh-server"
              [attr.data-variant]="s.id"
              [class.dh-server--selected]="selectedServer() === s.id"
              [attr.for]="'model-rs-' + s.id"
            >
              <input
                class="dh-server__input"
                type="radio"
                name="model-resource-server"
                [id]="'model-rs-' + s.id"
                [value]="s.id"
                [checked]="selectedServer() === s.id"
                (change)="selectServer(s.id)"
              />
              <span class="dh-server__focus" aria-hidden="true"></span>
              <span class="dh-server__header">
                <span class="dh-server__icon-box" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path d="M14 2V8H20M12 18V12M9 15H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
                <span class="dh-server__badge dh-server__badge--placeholder" aria-hidden="true"></span>
              </span>
              <span class="dh-server__body">
                <span class="dh-server__label">{{ s.title }}</span>
                <span class="dh-server__desc">{{ s.description }}</span>
              </span>
            </label>
          }
        </div>
      </div>
    </section>

    <div class="dh-meta-flow" @modelMetaReveal>
      <div class="dh-divider" role="presentation"></div>

      <section class="dh-section" aria-labelledby="model-meta-heading">
        <h2 id="model-meta-heading" class="dh-section__title">Add metadata</h2>

        <fieldset class="dh-fieldset">
          <legend class="dh-sr-only">Metadata entry</legend>

          <div class="dh-meta-grid">
            @for (path of metadataPaths; track path.mode) {
              <label
                class="dh-path dh-path--row"
                [class.dh-path--selected]="metadataMode() === path.mode"
                [attr.for]="'model-mode-' + path.mode"
              >
                <input
                  class="dh-path__input"
                  type="radio"
                  name="model-metadata-mode"
                  [id]="'model-mode-' + path.mode"
                  [value]="path.mode"
                  [checked]="metadataMode() === path.mode"
                  (change)="selectMetadataMode(path.mode)"
                />
                <span class="dh-path__focus" aria-hidden="true"></span>

                <span class="dh-path__icon-box" aria-hidden="true">
                  @if (path.icon === 'pencil') {
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  } @else {
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M7 14L12 9L17 14"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12 9V21"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  }
                </span>

                <span class="dh-path__main">
                  <span class="dh-path__title">{{ path.title }}</span>
                  <span class="dh-path__desc">{{ path.description }}</span>
                </span>

                <span class="dh-path__mark" aria-hidden="true">
                  <span class="dh-path__mark-dot"></span>
                </span>
              </label>
            }
          </div>
        </fieldset>
      </section>
    </div>

    <div class="dh-actions">
      <button
        type="button"
        class="dh-actions__btn dh-actions__btn--primary"
        [disabled]="!canContinue()"
        (click)="onContinue()"
      >
        Continue
      </button>
    </div>
  </div>
</div>
